// CartView — marketplace cart listing: line items, promo code, totals.
//
// Extracted from MarketplaceEmbed.tsx (Task 4, TD-269 Sub-project D UX
// split). Reads cart state from useCart() directly rather than taking it as
// a prop — it is a context consumer, same as the parent router was.
//
// The "Proceed to checkout" click used to reset seven pieces of checkout
// state (step, sub-step, billing selection, completed steps) before
// routing. That reset logic is checkout's responsibility, not the cart's —
// it now lives in the parent's onProceedToCheckout handler (Task 5 moves it
// again, into CheckoutFlow). CartView only calls onProceedToCheckout(); it
// does not know about checkout state.
//
// Inline styles only — no Tailwind. NO imports from portal/.

import { useState, useEffect, useMemo, Fragment } from "react";
import { EmbedButton, EmbedLink, EmbedText, useEmbedTokens } from "../../embed-shared/primitives";
import { useCart } from "../../embed-shared/cart-context";
import { embedApiFetch } from "../../embed-shared/api-client";
import type { CartLine, Product } from "../../embed-shared/types";
import { CartLineItem } from "./CartLineItem";
import { CartSummary } from "./CartSummary";
import { PromoCodeInput } from "./PromoCodeInput";
import { typeLabel } from "./ProductCard";
import { productResolvesRecurring } from "./ProductDetailView";
import { StoreDraftPanel, StorePicker } from "./StorePicker";
import { listStores, type CustomerStoreWire } from "./store-api";
import { clearStoreDraft, findDraftTargetLine, readStoreDraft, type StagedStoreDraft } from "./store-staging";
import { detailInsetStyle, useViewportWidth } from "./detail-inset";

/**
 * Whether this cart line RESOLVES a recurring component — the criterion
 * checkout's store gate keys off (design §6), read from the line wire: the
 * interval is always emitted (null = does not recur) and Task 7 added the
 * whole-line per-period amount (explicit null = does not recur). OR, not
 * AND: for the store REQUIREMENT we prefer surfacing the picker over missing
 * the checkout gate, so any recurrence signal qualifies the line.
 *
 * ⚠️ AMENDED (device-count unit, Task 10, Task-9 review finding (b)) — the
 * parameter `catalogEntry` is new. ORIGINAL: `lineNeedsStore(line)` keyed
 * ONLY off the line's STORED resolution. CURRENT: the LIVE catalog
 * resolution is an OR-arm. A product that GAINED a recurring cell after its
 * line was added carries a stale one-time snapshot on the wire (the stored
 * resolution is add-time), so the stored signal shows no store control
 * while checkout re-resolves LIVE and refuses the cart with 422
 * STORE_REQUIRED_FOR_RECURRING (Task 8) — an unexplainable dead end. The
 * catalog lookup this view already runs for presentation names/images
 * carries the live resolution; keying the control off it too surfaces the
 * picker for exactly those stale lines. OR is the safe direction: a line
 * whose stored signal says recurring always shows the control regardless of
 * what the catalog page returned.
 */
function lineNeedsStore(
  line: CartLine,
  catalogEntry?: { resolvesRecurring?: boolean },
): boolean {
  if (line.recurring_interval != null || line.recurring_amount != null) return true;
  return catalogEntry?.resolvesRecurring === true;
}

export interface CartViewProps {
  onContinueShopping: () => void;
  onProceedToCheckout: () => void;
  /** Opens a product's detail page (Apple-bag rows link to the product). */
  onOpenProduct?: (slug: string) => void;
}

/**
 * Largest page the catalog endpoint will serve — HandleCatalogList's
 * clampLimit caps `limit` at 50 (customer_handler.go). The guest staging
 * buffer is capped at 50 entries too (spec §6.1), so one page covers every
 * staged line for any catalog up to 50 active products. Beyond that, a staged
 * product may fall outside this page and keeps the positional fallback below —
 * the fallback is what makes the partial-resolution case honest rather than
 * silently wrong.
 */
const CATALOG_NAME_LOOKUP_LIMIT = 50;

/** Catalog-resolved presentation data for a cart line: the guest name (spec
 *  §6.2), the lead image + type label every row wants (2026-08-16), the
 *  slug the AUTHENTICATED wire omits (operator report f: model.CartLine has
 *  no name/slug fields, so auth rows resolve name AND link from here), and
 *  — since Task 10 — the two pricing-shape flags the device-count unit
 *  needs on the cart surface: `resolvesRecurring` (the LIVE recurrence the
 *  stored line snapshot can lag behind — finding (b)) and `devicePriced`
 *  (quantity IS the device count on such a line — ruling 1/4). */
type CartCatalogEntry = {
  name: string;
  slug: string;
  image?: string;
  typeLabel: string;
  resolvesRecurring: boolean;
  devicePriced: boolean;
};

export function CartView({ onContinueShopping, onProceedToCheckout, onOpenProduct }: CartViewProps) {
  const t = useEmbedTokens();
  const viewportWidth = useViewportWidth();
  const { state: cartState, isGuest, updateLineQty, removeLine, applyPromo, clearPromo, setLineStore } = useCart();
  const cart = cartState.cart;

  // -- Store association (device-count unit, Task 9, design §6) -----------
  //
  // Signed-in view only: guests see no store UI — their captured draft is a
  // POST-LOGIN concern (ruling 9(a)) and the guest cart lines carry no
  // recurrence data anyway.
  /** store_id → store, for rendering assigned-store names. */
  const [storesById, setStoresById] = useState<Record<string, CustomerStoreWire>>({});
  /** Which line's StorePicker is open. */
  const [pickerLineId, setPickerLineId] = useState<string | null>(null);
  /** A staged pre-login draft pending reconciliation against this cart. */
  const [reconcileDraft, setReconcileDraft] = useState<StagedStoreDraft | null>(null);

  // Resolve store names once per signed-in visit. Name resolution is
  // cosmetic — a failed fetch degrades to "Store selected" and the
  // association itself is unchanged (it lives on the server).
  useEffect(() => {
    if (isGuest) return;
    let cancelled = false;
    (async () => {
      try {
        const stores = await listStores();
        if (cancelled) return;
        const byId: Record<string, CustomerStoreWire> = {};
        for (const s of stores) byId[s.id] = s;
        setStoresById(byId);
      } catch {
        // Cosmetic degradation only — see above.
      }
    })();
    return () => { cancelled = true; };
  }, [isGuest]);

  // Post-login draft reconciliation (design §6.2): surface the staged draft
  // once its line is in THIS cart and still store-less. If the line is gone
  // (replay failed, or the customer removed it) or already associated (e.g.
  // picked on the PDP after login), the draft's purpose is SPENT — clear it
  // rather than blocking on it; keeping a stale draft would re-prompt against
  // a line that no longer needs it.
  //
  // ⚠️ AMENDED (device-count unit, Task 10, Task-9 review finding (a)) — the
  // binding now goes through findDraftTargetLine (store-staging.ts): the
  // draft targets a same-product line with NO store yet (the newest such
  // line), not the FIRST same-product match. ORIGINAL: `cart.lines.find(
  // (l) => l.product_id === draft.product_id)` then `!match || match.store_id`
  // — unambiguous only while the server merged same-product adds into one
  // line; under the add-again flow's multiple lines per product a first
  // match that already carries a store would wrongly declare the draft
  // spent while its store-less sibling still needs it.
  //
  // ⚠️ Guarded on `cart` being loaded: on a signed-in mount the effect first
  // fires BEFORE the cart fetch resolves (cart === null), and an unguarded
  // "no matching line" would CLEAR the draft before the line it belongs to
  // ever arrived — the panel would never show. Only a LOADED cart may judge
  // the draft spent.
  useEffect(() => {
    if (isGuest || !cart) return;
    const draft = reconcileDraft ?? readStoreDraft();
    if (!draft) return;
    const match = findDraftTargetLine(cart.lines, draft);
    if (!match) {
      if (reconcileDraft) setReconcileDraft(null);
      clearStoreDraft();
      return;
    }
    if (!reconcileDraft) setReconcileDraft(draft);
  }, [isGuest, cart, reconcileDraft]);

  // Assigning (or reassigning) a line's store: the StorePicker submits the
  // store's ID; the PATCH needs the line's current quantity alongside it
  // (Task-7 wire contract). One await — the multi-step sequencing lives in
  // the panel/picker, not here.
  async function handleAssignStore(line: CartLine, store: CustomerStoreWire) {
    setActionError(null);
    setPickerLineId(null);
    const result = await setLineStore(line.id, store.id, line.quantity);
    if (!result.ok) {
      setActionError(result.error ?? "We couldn't save your store choice. Please try again.");
    }
  }

  // Live-defect fix (this task's brief): updateLineQty/removeLine now
  // resolve an explicit AddLineResult (see cart-context.tsx), mirroring
  // addLine's Task 15 fix. cartState.error is NOT a reliable signal for a
  // per-action failure here: on the authenticated path, a failed
  // PATCH/DELETE whose rollback GET succeeds dispatches FETCH_SUCCESS,
  // which clears cartState.error even though the action itself failed (see
  // cart-context's updateLineQty/removeLine doc comments). actionError is
  // populated straight from each call's own return value, independent of
  // that ambient dispatch — the same reasoning ProductDetailView already
  // applies to addLine.
  const [actionError, setActionError] = useState<string | null>(null);

  async function handleUpdateQty(lineId: string, quantity: number) {
    // Clear any STRANDED actionError from a prior action up front (TD-314):
    // without this, a stale per-action error persists until the NEXT action's
    // result resolves, shadowing whatever the current action produces.
    setActionError(null);
    const result = await updateLineQty(lineId, quantity);
    setActionError(result.ok ? null : (result.error ?? "We couldn't update your cart. Please try again."));
  }

  async function handleRemove(lineId: string) {
    setActionError(null);
    const result = await removeLine(lineId);
    setActionError(result.ok ? null : (result.error ?? "We couldn't update your cart. Please try again."));
  }

  // -- Catalog presentation info (spec §6.2 names + 2026-08-16 Apple cart) --
  //
  // A guest line is projected from the staging buffer, which holds only
  // {product_id, quantity} (spec §6.1) — so product_name is "" and every row
  // renders as a nameless <h4> with buttons announced as "Remove  from cart".
  // Three staged items become three visually identical rows and a screen
  // reader cannot tell which Remove removes what.
  //
  // Since the 2026-08-16 Apple-style cart directive, the SAME lookup also
  // resolves the lead image + product type for EVERY line (guest AND
  // authenticated): the cart wire carries name/slug/money but neither images
  // nor type. Everything comes from the PUBLIC catalog endpoint — the same
  // call CatalogView makes, no bearer required, so it works signed out. It is
  // a read of data the visitor can already browse, not a re-introduction of
  // client-side pricing: only presentation fields are taken.
  const [catalogInfo, setCatalogInfo] = useState<Record<string, CartCatalogEntry>>({});

  // Product ids still missing presentation info. Keying the effect on THIS
  // (rather than on the line list) means removing a line does not refire the
  // lookup, and a product that genuinely isn't in the fetched page does not
  // loop: the key is unchanged after a resolution attempt that didn't cover
  // it.
  const unresolvedKey = useMemo(() => {
    if (!cart) return "";
    return cart.lines
      .map((l) => l.product_id)
      // hasOwnProperty, NOT the `in` operator (TD-314): `in` walks the
      // prototype chain, so a product_id like "toString" would resolve to an
      // inherited function and be treated as already-resolved, dropping the
      // line from the name-lookup key and leaving it nameless.
      .filter((id) => !Object.prototype.hasOwnProperty.call(catalogInfo, id))
      .join(",");
  }, [cart, catalogInfo]);

  useEffect(() => {
    if (!unresolvedKey) return;
    let cancelled = false;
    (async () => {
      try {
        const products = await embedApiFetch<Product[]>("/api/v1/marketplace/catalog", {
          query: { limit: String(CATALOG_NAME_LOOKUP_LIMIT) },
        });
        // Array.isArray guard + mapping OUTSIDE the state updater: the updater
        // runs during dispatch (outside this try/catch), so iterating there
        // escapes the catch — a non-array resolution would surface as an
        // uncaught render error instead of the cosmetic no-op below.
        if (cancelled || !Array.isArray(products)) return;
        const resolved: Record<string, CartCatalogEntry> = {};
        for (const p of products) {
          resolved[p.id] = {
            name: p.name,
            slug: p.slug,
            image: p.images?.[0]?.url,
            typeLabel: typeLabel(p.product_type),
            // LIVE pricing shape from the same public catalog page (Task
            // 10): recurrence for the store control (finding (b) — the
            // stored line snapshot is add-time and can lag a cell edit),
            // device basis for the line's device wording (ruling 1/4).
            resolvesRecurring: productResolvesRecurring(p),
            devicePriced: (p.price?.components ?? []).some((c) => c.basis === "device"),
          };
        }
        setCatalogInfo((prev) => ({ ...prev, ...resolved }));
      } catch {
        // Presentation info is cosmetic — the positional fallback below keeps
        // every row identifiable and a missing image just renders no
        // thumbnail. Surfacing an error here would tell the customer their
        // cart is broken when it is not (their staged lines are intact and
        // replay on sign-in regardless).
      }
    })();
    return () => { cancelled = true; };
  }, [unresolvedKey]);

  /**
   * Name to render for a guest line: the resolved catalog name, else a stable
   * positional label so the row and its three controls still have a distinct
   * accessible name. Returns undefined for an authenticated line, whose
   * server-supplied product_name is already correct.
   */
  function guestDisplayName(productId: string, index: number): string | undefined {
    if (!isGuest) return undefined;
    return catalogInfo[productId]?.name ?? `Item ${index + 1}`;
  }

  // "Loaded, but nothing to show" covers BOTH a signed-in cart that fetched
  // empty AND a guest who has never staged anything — cart-context's mount
  // effect now dispatches SET_GUEST_LINES unconditionally (fixed at the
  // producer, see cart-context.tsx), so a fresh guest reaches
  // status === "ready" with an empty cart exactly like a signed-in visitor
  // whose cart fetch returned empty.
  //
  // Gated on status === "ready" specifically, NOT "not loading and not
  // error" — those two are not the complement of "ready". INITIAL_STATE is
  // {status: "idle", cart: null} (cart-context.tsx), and CartProvider's
  // mount effect updates it asynchronously relative to the first commit
  // (in a real browser, passive effects run after paint) — a broader
  // exclusion list previously let "idle" fall through as "safe to render
  // empty", which committed "Your cart is empty." + a "Continue shopping"
  // CTA for EVERY mount, including an authenticated user whose cart is
  // about to load full (review round 1 finding on this task). Requiring
  // "ready" means the empty state only ever shows once the fetch (or the
  // guest hydrate, now unconditional) has actually settled.
  const showEmptyCart =
    cartState.status === "ready" && (!cart || cart.lines.length === 0);

  // Precedence is cartState.error ?? actionError (TD-314): a fresher ambient
  // error (e.g. a fetch failure) must win over a stranded per-action error.
  // actionError only carries weight when there is no ambient error — which is
  // exactly the case it exists for (a failed PATCH/DELETE whose rollback GET
  // succeeds dispatches FETCH_SUCCESS and clears cartState.error; the explicit
  // return value captured in actionError is then the only signal).
  const displayError = cartState.error ?? actionError;

  return (
    <div style={detailInsetStyle(t, viewportWidth)}>
      <div style={{ marginBottom: t.spacing.md }}>
        <EmbedButton type="button" variant="ghost" size="sm" onClick={onContinueShopping}>
          &larr; Continue shopping
        </EmbedButton>
      </div>
      <h2 style={{ fontSize: t.fontSize.xl, fontWeight: 700, color: t.colors.text, margin: `0 0 ${t.spacing.md}px 0` }}>
        Your cart
      </h2>
      {cartState.status === "loading" && <p role="status" style={{ color: t.colors.textSecondary }}>Loading cart...</p>}
      {displayError && <p role="alert" style={{ color: t.colors.error }}>{displayError}</p>}
      {showEmptyCart && (
        <div role="status" style={{ textAlign: "center", padding: t.spacing.xl }}>
          <p style={{ color: t.colors.textSecondary, fontSize: t.fontSize.md, margin: `0 0 ${t.spacing.md}px 0` }}>
            Your cart is empty.
          </p>
          <EmbedButton type="button" variant="primary" onClick={onContinueShopping}>
            Continue shopping
          </EmbedButton>
        </div>
      )}
      {cart && cart.lines.length > 0 && (
        <>
          {/* Post-login reconciliation of a pre-login store draft (§6.2):
              shows the candidate, waits for the customer to CONFIRM — the
              panel never auto-associates and never auto-creates. Rendered
              only while the draft's line is still in this cart store-less
              (the effect above clears spent drafts). */}
          {reconcileDraft &&
            (() => {
              // Same stronger binding as the spent-check effect above
              // (finding (a)): the store-less same-product line, newest
              // first — one source of truth for both sites.
              const draftLine = findDraftTargetLine(cart.lines, reconcileDraft);
              if (!draftLine) return null;
              return (
                <StoreDraftPanel
                  draft={reconcileDraft}
                  onApplyStore={async (store) => setLineStore(draftLine.id, store.id, draftLine.quantity)}
                  onDone={() => {
                    clearStoreDraft();
                    setReconcileDraft(null);
                  }}
                />
              );
            })()}
          {cart.lines.map((line, index) => (
            <Fragment key={line.id}>
              <CartLineItem
                line={line}
                displayName={guestDisplayName(line.product_id, index)}
                // Guest money is all zero by construction — see below.
                showLineTotal={!isGuest}
                catalogInfo={catalogInfo[line.product_id]}
                onOpenProduct={onOpenProduct}
                onUpdateQty={handleUpdateQty}
                onRemove={handleRemove}
              />
              {/* Per-line store association (design §6). Rendered for a
                  signed-in customer on a line that resolves a recurring
                  component: without one, checkout refuses the cart (Task 8's
                  422 STORE_REQUIRED_FOR_RECURRING) — this control is the UX
                  that prevents hitting that gate. One-time lines show
                  nothing (ruling 10). Since Task 10 (finding (b)) the
                  predicate ALSO keys off the LIVE catalog resolution —
                  see lineNeedsStore's AMENDED note. */}
              {!isGuest && lineNeedsStore(line, catalogInfo[line.product_id]) && (
                <div style={{ display: "flex", alignItems: "center", gap: t.spacing.sm, flexWrap: "wrap", marginBottom: t.spacing.md }}>
                  {line.store_id ? (
                    <>
                      <EmbedText size="sm">
                        Store: {storesById[line.store_id]?.name ?? "selected"}
                      </EmbedText>
                      <EmbedLink
                        type="button"
                        onClick={() => setPickerLineId(pickerLineId === line.id ? null : line.id)}
                      >
                        Change
                      </EmbedLink>
                    </>
                  ) : (
                    <>
                      <EmbedText size="sm" tone="error">
                        A store is required for this subscription.
                      </EmbedText>
                      <EmbedButton
                        type="button"
                        variant="secondary"
                        size="sm"
                        onClick={() => setPickerLineId(pickerLineId === line.id ? null : line.id)}
                      >
                        Choose a store
                      </EmbedButton>
                    </>
                  )}
                  {pickerLineId === line.id && (
                    <StorePicker
                      onPick={(store) => void handleAssignStore(line, store)}
                      onCancel={() => setPickerLineId(null)}
                    />
                  )}
                </div>
              )}
            </Fragment>
          ))}
          {/* applyPromo/clearPromo PUT and DELETE the bearer-protected
              /me/cart/promo, which can only 401 signed out. Spec §6.2: the
              promo input is HIDDEN for a guest, not shown-and-broken. */}
          {!isGuest && (
            <PromoCodeInput
              appliedCode={cart.promo_code}
              onApply={async (code) => { await applyPromo(code); }}
              onClear={clearPromo}
            />
          )}
          {/* Spec §6.2: money is NOT computed locally for a guest. Every field
              on a guest cart is 0 (SET_GUEST_LINES spreads EMPTY_CART), so
              CartSummary would render "Subtotal $0.00 / Tax $0.00 / Total
              $0.00" — invented numbers — and, because its shipping row
              branches on `shipping === 0`, an unconditional shipping promise
              asserted before any shipping calculation exists. */}
          {isGuest ? (
            <div
              style={{
                borderTop: `1px solid ${t.colors.cardBorder}`,
                padding: `${t.spacing.md}px 0`,
              }}
            >
              <p style={{ margin: 0, fontSize: t.fontSize.sm, color: t.colors.textSecondary }}>
                Pricing calculated at checkout
              </p>
            </div>
          ) : (
            <CartSummary
              subtotal={cart.subtotal}
              taxEstimate={cart.tax_estimate}
              promoDiscount={cart.promo_discount}
              shipping={cart.shipping}
              total={cart.total}
              promoCode={cart.promo_code}
            />
          )}
          <div style={{ marginTop: t.spacing.md }}>
            <EmbedButton type="button" variant="primary" fullWidth onClick={onProceedToCheckout}>
              Proceed to checkout
            </EmbedButton>
          </div>
        </>
      )}
    </div>
  );
}
